import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { PostLink } from '../../../models/ContentPlan';

// Компонент для отображения и удаления ссылок в редакторе
interface LinksEditorProps {
  links: PostLink[];
  onChange: (newLinks: PostLink[]) => void;
  className?: string;
}

export const LinksEditor: React.FC<LinksEditorProps> = ({ links, onChange, className = '' }) => {
  const [linkToDelete, setLinkToDelete] = useState<{ index: number; link: PostLink } | null>(null);

  const confirmRemoveLink = (index: number) => {
    const newLinks = links.filter((_, i) => i !== index);
    onChange(newLinks);
    setLinkToDelete(null);
  };

  const handleRemoveClick = (index: number, link: PostLink) => {
    setLinkToDelete({ index, link });
  };

  if (!links || links.length === 0) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Ссылки добавляются ИИ при генерации контента
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-2 ${className}`}>
        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
          Ссылки:
        </div>
        
        {links.map((link, index) => (
          <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {link.name || 'Без названия'}
              </div>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block"
              >
                {link.url}
              </a>
            </div>
            <button
              onClick={() => handleRemoveClick(index, link)}
              className="ml-3 p-1 text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
              title="Удалить ссылку"
              aria-label={`Удалить ссылку ${link.name}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        ))}
      </div>

      {linkToDelete && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-[20000] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Удалить ссылку?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Вы уверены, что хотите удалить ссылку "{linkToDelete.link.name || linkToDelete.link.url}"?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setLinkToDelete(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => confirmRemoveLink(linkToDelete.index)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Компонент для отображения ссылок в превью (только чтение)
interface LinksDisplayProps {
  links: PostLink[];
  className?: string;
}

export const LinksDisplay: React.FC<LinksDisplayProps> = ({ links, className = '' }) => {
  const validLinks = (links || []).filter(l => l && typeof l.url === 'string' && l.url.trim() !== '');
  if (validLinks.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
        Ссылки:
      </div>
      <div className="space-y-1">
        {validLinks.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline text-xs group"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            <span className="truncate">{link.name || link.url}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 01-2-2h6"></path>
              <polyline points="15,3 21,3 21,9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </a>
        ))}
      </div>
    </div>
  );
};

